import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '../../../auth-lib';
import { Button, Card, StatusPill } from '../../../design-system';
import '../../styles/TallyPanel.css';

// Every company's sync state, on the screen the section opens on.
//
// ⚠️ THIS EXISTS BECAUSE SYNCING WENT MANUAL. While a cron ran the syncs
// overnight there was nothing to watch and nobody watching. Now a person drives
// it — and "which of my companies is stuck?" was answerable only by picking each
// one out of a dropdown on the Books tab in turn. A firm with forty connections
// had forty places to look and no reason to look at any of them.
//
// ⚠️ IT SHOWS WHAT NEEDS A PERSON, NOT EVERYTHING. A company that is simply
// idle is not a problem — syncing is manual, so idle is the resting state. The
// rows here are work that is queued with nobody to do it, or a last attempt that
// failed. `needsAttention` is decided by the SERVER so this list and the counts
// above it cannot drift apart.
//
// ⚠️ IT RENDERS NOTHING WHEN THERE ARE NO CONNECTIONS. A firm that has not
// scanned a machine yet should see the empty state it already has, not an empty
// status panel above it explaining that nothing is wrong.

const POLL_MS = 5000;

/** "2m ago" — what somebody watching actually reads. */
function ago(iso) {
  if (!iso) return 'never';
  const secs = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

function TallySyncOverview() {
  const [, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/tally/activity');
      setData(res.data);
    } catch {
      // Silent. This is a status panel above the real content — a red toast
      // because a summary could not load would be louder than the thing it
      // summarises, and the page below it still works.
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ⚠️ POLLS ONLY WHILE SOMETHING IS MOVING, and re-arms from the answer rather
  // than on a fixed interval — so the last poll after the queue drains is the
  // one that stops it. This is the landing screen of the section; a setInterval
  // here would hammer the API for as long as anybody left the tab open.
  const busy = !!data && (data.totals.syncing > 0 || data.totals.queued > 0);
  useEffect(() => {
    clearTimeout(timer.current);
    if (!busy) return undefined;
    timer.current = setTimeout(() => { load(); }, POLL_MS);
    return () => clearTimeout(timer.current);
  }, [busy, data, load]);

  useEffect(() => () => clearTimeout(timer.current), []);

  /** Straight to that company's Sync view — the queue, the machine, the error. */
  const openSync = (connectionId) => {
    setParams({ tab: 'books', company: connectionId, view: 'sync' }, { replace: false });
  };

  // Nothing connected yet: say nothing at all.
  if (!data || !data.totals.connections) return null;

  const { totals, rows } = data;
  const attention = rows.filter((r) => r.needsAttention);
  const shown = showAll ? rows : attention;

  // Everything idle and nothing wrong — one quiet line, not a panel of zeroes.
  if (!attention.length && !totals.syncing && !totals.queued && !showAll) {
    return (
      <Card>
        <div className="tally-head">
          <p className="tally-muted">
            <StatusPill variant="success" label="All quiet" />{' '}
            {totals.connections} connected {totals.connections === 1 ? 'company' : 'companies'},
            nothing syncing and nothing waiting. Syncing is manual — open a company
            on the Books tab and press Sync now.
          </p>
          <Button variant="ghost" onClick={() => setShowAll(true)}>Show all</Button>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="tally-head">
        <div>
          <h3 className="tally-section-title">Sync status</h3>
          <p className="tally-muted">
            Nothing syncs on its own. These are the companies with work in progress
            or something that needs a person.
          </p>
        </div>
        <div className="tally-actions">
          <Button variant="ghost" onClick={load}>Refresh</Button>
          <Button variant="ghost" onClick={() => setShowAll((v) => !v)}>
            {showAll ? 'Only what needs attention' : `Show all ${totals.connections}`}
          </Button>
        </div>
      </div>

      <div className="tally-status-row">
        {totals.syncing > 0 && <StatusPill variant="info" label={`${totals.syncing} syncing`} />}
        {totals.queued > 0 && <StatusPill variant="default" label={`${totals.queued} job(s) queued`} />}
        {totals.offline > 0 && (
          // The most actionable number here: work is waiting for machines that
          // are not listening.
          <StatusPill variant="danger" label={`${totals.offline} waiting on an offline machine`} />
        )}
        {totals.failing > 0 && <StatusPill variant="danger" label={`${totals.failing} with an error`} />}
        {!totals.syncing && !totals.queued && !totals.failing && !totals.offline && (
          <StatusPill variant="success" label="All quiet" />
        )}
      </div>

      {!shown.length ? (
        <p className="tally-muted">Nothing needs attention.</p>
      ) : (
        <ul className="tally-sync-rows">
          {shown.map((r) => (
            <li key={r.connectionId} className="tally-sync-row">
              <button
                type="button"
                className="tally-linkish tally-sync-open"
                onClick={() => openSync(r.connectionId)}
              >
                {/* ⚠️ THE CLIENT FIRST, THE TALLY NAME AFTER, and both always —
                    a firm routinely names a company differently from the client
                    it belongs to, and showing the Tally name only when it
                    differs makes the differing rows look like a rendering quirk.
                    Same rule as the Books picker. */}
                <span className="tally-sync-name">
                  {r.clientName ? `${r.clientCode} ${r.clientName} — ` : ''}{r.companyName}
                </span>
                <span className="tally-sync-detail">
                  {r.running ? 'Syncing now' : null}
                  {!r.running && (r.queued + r.claimed) > 0
                    ? `${r.queued + r.claimed} job(s) waiting`
                    : null}
                  {!r.running && !(r.queued + r.claimed)
                    ? `Last synced ${ago(r.lastSuccessfulSyncAt)}`
                    : null}
                  {r.machineName ? ` · ${r.machineName}` : ''}
                </span>
                {/* Verbatim, per the rule this module keeps everywhere: "Tally is
                    not running" and "no company is open" are different problems
                    with different fixes and different people to ring. */}
                {r.lastError ? <span className="tally-sync-err">{r.lastError}</span> : null}
              </button>

              <span className="tally-sync-pills">
                {r.status !== 'active' && <StatusPill variant="warning" label={r.status} />}
                {(r.queued + r.claimed) > 0 && !r.machineOnline && (
                  <StatusPill variant="danger" label="Machine offline" />
                )}
                {r.running && <StatusPill variant="info" label="Syncing" />}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export default TallySyncOverview;
